import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHome, FaExclamationTriangle, FaArrowLeft, FaFrown } from 'react-icons/fa';
import { FiAlertCircle } from 'react-icons/fi';

const NotFound = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex flex-col items-center justify-center p-4">
      <div className="max-w-4xl w-full text-center">
        
        {/* Animated 404 Number */}
        <div className="relative mb-8">
          <div className="text-[180px] md:text-[250px] font-bold text-gray-300 dark:text-gray-700 opacity-80 animate-pulse">
            404
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-[180px] md:text-[250px] font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent animate-bounce">
              404
            </div>
          </div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <FaFrown className="w-32 h-32 text-yellow-500 opacity-20 animate-spin-slow" />
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-2xl transform transition-all duration-500 hover:scale-[1.02]">
          
          {/* Icon */}
          <div className="mb-6 inline-block p-4 bg-red-100 dark:bg-red-900/30 rounded-full animate-pulse">
            <FiAlertCircle className="w-16 h-16 text-red-500 dark:text-red-400" />
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white mb-4 animate-fade-in-up">
            Page Not Found
          </h1>

          {/* Description */}
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
            Oops! The page you're looking for seems to have wandered off into the digital void. 
            It might have been moved, deleted, or never existed in the first place.
          </p>

          {/* Stats/Info Box */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 animate-fade-in-up animation-delay-400">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl border border-blue-100 dark:border-blue-800">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">404</div>
              <div className="text-gray-700 dark:text-gray-300">Error Code</div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-xl border border-purple-100 dark:border-purple-800">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">Lost</div>
              <div className="text-gray-700 dark:text-gray-300">Page Status</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-xl border border-green-100 dark:border-green-800">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">Found</div>
              <div className="text-gray-700 dark:text-gray-300">You Are Here</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-600">
            <button
              onClick={handleGoBack}
              className="group flex items-center justify-center gap-3 px-8 py-4 bg-gray-800 dark:bg-gray-700 text-white rounded-xl hover:bg-gray-900 dark:hover:bg-gray-600 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
            >
              <FaArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Go Back</span>
            </button>

            <button
              onClick={handleGoHome}
              className="group flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/25"
            >
              <FaHome className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span className="font-semibold">Back to Homepage</span>
            </button>
          </div>

          {/* Search Suggestion */}
          <div className="mt-10 p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl animate-fade-in-up animation-delay-800">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Can't find what you're looking for? Try these:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[ 'About-Us', 'ContactUs'].map((item) => (
                <button
                  key={item}
                  onClick={() => navigate(`/${item.toLowerCase()}`)}
                  className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Footer Note */}
        <p className="mt-8 text-gray-500 dark:text-gray-400 animate-fade-in-up animation-delay-1000">
          If you believe this is an error, please contact support
        </p>

      </div>

      {/* Floating Elements for Decoration */}
      <div className="fixed top-1/4 left-10 w-4 h-4 bg-blue-400 rounded-full opacity-30 animate-float"></div>
      <div className="fixed top-1/3 right-20 w-6 h-6 bg-purple-400 rounded-full opacity-20 animate-float animation-delay-1000"></div>
      <div className="fixed bottom-1/4 left-1/4 w-3 h-3 bg-red-400 rounded-full opacity-30 animate-float animation-delay-2000"></div>
      <div className="fixed bottom-1/3 right-1/3 w-5 h-5 bg-green-400 rounded-full opacity-20 animate-float animation-delay-3000"></div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        .animation-delay-400 {
          animation-delay: 400ms;
        }
        .animation-delay-600 {
          animation-delay: 600ms;
        }
        .animation-delay-800 {
          animation-delay: 800ms;
        }
        .animation-delay-1000 {
          animation-delay: 1000ms;
        }
        .animation-delay-2000 {
          animation-delay: 2000ms;
        }
        .animation-delay-3000 {
          animation-delay: 3000ms;
        }
      `}</style>
    </div>
  );
};

export default NotFound;